import {
  CustomerInsuranceSummaryActionPayload,
  CustomerInsuranceSummaryErrorPayload,
  CustomerInsuranceSummaryResponse
} from 'actions/getCustomerInsuranceSummaryAction';
import { HpAction, HpTemplateActionTypes } from '../actions/actionTypes';
import { CustomerDataState, getInitialState } from './initialState';
import { ReducerProcessor } from './reducerProcessor';
import { AUTH_TYPE } from 'interfaces/authTypes';
import { CustomerPolicyDetailsSuccessPayload } from 'actions/getCustomerPolicyAction';
import { FullPolicyData, Policy, PolicyDetail } from 'interfaces/customerDataTypes';

const getFullPolicyData = (
  policyDetail: PolicyDetail,
  policyDetailsId: string,
  historyId: number,
  policies?: Policy[]
): FullPolicyData | undefined => {
  const matchingPolicy =
    policies && policies.find((policy) => policy.policyDetailsId === policyDetailsId && policy.historyId === historyId);

  const selectedPolicy = matchingPolicy
    ? {
        ...policyDetail,
        ...matchingPolicy
      }
    : undefined;

  return selectedPolicy;
};

const processor = new ReducerProcessor<CustomerDataState, HpTemplateActionTypes>('customer');

processor.add('CUSTOMER_POLICY_DETAILS_LOADING', (state) => {
  return {
    ...state,
    policyDetailsLoading: true
  };
});

processor.add('CUSTOMER_POLICY_DETAILS_ERROR', (state, action) => {
  return {
    ...state,
    policyDetailsLoading: false,
    policyDetailsError: action.payload,
    isPolicyDetailsError: true,
    selectedPolicy: undefined
  };
});

processor.add<HpAction<CustomerPolicyDetailsSuccessPayload>>('CUSTOMER_POLICY_DETAILS_SUCCESS', (state, action) => {
  const { policyDetail, policyDetailsId, historyId } = action.payload as CustomerPolicyDetailsSuccessPayload;

  const selectedPolicy = getFullPolicyData(policyDetail, policyDetailsId, historyId, state.policies);

  return {
    ...state,
    policyDetailsLoading: false,
    isPolicyDetailsError: false,
    selectedPolicy
  };
});

processor.add<HpAction<CustomerInsuranceSummaryResponse>>('TOGGLE_AUTO_RENEW_SUCCESS', (state, action) => {
  const { policies } = action.payload as CustomerInsuranceSummaryResponse;
  const selectedPolicy = state.selectedPolicy as FullPolicyData;
  const { policyDetailsId, historyId } = selectedPolicy;
  const newSelectedelectedPolicy = getFullPolicyData(selectedPolicy, policyDetailsId, historyId, policies);

  return {
    ...state,
    selectedPolicy: newSelectedelectedPolicy,
    toggleAutoRenewLoading: false,
    policies
  };
});

processor.add('TOGGLE_AUTO_RENEW_ERROR', (state, action) => {
  return {
    ...state,
    toggleAutoRenewLoading: false,
    toggleAutoRenewalError: action.payload
  };
});

processor.add('TOGGLE_AUTO_RENEW_LOADING', (state) => {
  return {
    ...state,
    toggleAutoRenewLoading: true
  };
});

processor.add('TOGGLE_PRINT_DOCUMENTS_SUCCESS', (state, action) => {
  const { policyDetail, policyDetailsId, historyId } = action.payload as CustomerPolicyDetailsSuccessPayload;
  const selectedPolicy = getFullPolicyData(policyDetail, policyDetailsId, historyId, state.policies);

  return {
    ...state,
    selectedPolicy,
    togglePrintDocumentsLoading: false
  };
});

processor.add('TOGGLE_PRINT_DOCUMENTS_ERROR', (state, action) => {
  return {
    ...state,
    togglePrintDocumentsLoading: false,
    togglePrintDocumentsError: action.payload
  };
});

processor.add('TOGGLE_PRINT_DOCUMENTS_LOADING', (state) => {
  return {
    ...state,
    togglePrintDocumentsLoading: true
  };
});

processor.add('CUSTOMER_INSURANCE_SUMMARY_LOADING', (state) => {
  return {
    ...state,
    customerInsuranceSummaryLoading: true
  };
});

processor.add<HpAction<CustomerInsuranceSummaryErrorPayload>>('CUSTOMER_INSURANCE_SUMMARY_ERROR', (state, action) => {
  const { authType, error } = action.payload as CustomerInsuranceSummaryErrorPayload;

  if (authType === AUTH_TYPE.AGENT && error === 'No data found for user') {
    return {
      ...state,
      customerInsuranceSummaryLoading: false,
      noPolicyFoundError: true
    };
  }

  if (error === 'There is no auth token in the session.') {
    return {
      ...state,
      customerInsuranceSummaryLoading: false
    };
  }

  return {
    ...state,
    customerInsuranceSummaryLoading: false,
    customerInsuranceSummaryError: true
  };
});

processor.add<HpAction<CustomerInsuranceSummaryResponse>>('CUSTOMER_INSURANCE_SUMMARY_SUCCESS', (state, action) => {
  const { authType, policies, quotes } = action.payload as CustomerInsuranceSummaryResponse;

  if (authType === AUTH_TYPE.AGENT && !policies) {
    return {
      ...state,
      customerInsuranceSummaryLoading: false,
      noPolicyFoundError: true,
      quotes: quotes
    };
  }

  let updatedSelectedPolicy = state.selectedPolicy;

  if (state.selectedPolicy) {
    const selectedPolicy = state.selectedPolicy as FullPolicyData;
    const { policyDetailsId, historyId } = selectedPolicy;
    updatedSelectedPolicy = getFullPolicyData(selectedPolicy, policyDetailsId, historyId, policies);
  }

  return {
    ...state,
    customerInsuranceSummaryLoading: false,
    customerInsuranceSummaryError: false,
    noPolicyFoundError: false,
    policies: policies,
    quotes: quotes,
    selectedPolicy: updatedSelectedPolicy
  };
});

export function customerDataReducer(
  state: CustomerDataState = getInitialState().customerData,
  action: HpAction<CustomerInsuranceSummaryActionPayload | CustomerPolicyDetailsSuccessPayload | string>
): CustomerDataState {
  return processor.process(state, action);
}
